import { Box } from "@mantine/core";
import React, { useEffect } from "react";
import { ButtonRadioSelect } from "@jasper/shared/stories/ButtonRadioSelect/ButtonRadioSelect";
import { Title } from "@jasper/shared/stories/Title/Title";
import DentalArchesSvg from "@jasper/shared/components/DentalArchesSvg";
import { STYLE } from "@jasper/shared/types/constants";
import { Controller, useFormContext } from "react-hook-form";
import { getAllTeeths } from "@jasper/shared/utils/product.utils";
import { Product } from "@jasper/shared";
import { ItemMaterials } from "@jasper/shared/types/enums";
import { HookPlacementBy } from "@jasper/shared/utils/workflows.utils";

export const HooksAndOptions: React.FC<{ product: Product }> = ({
  product,
}) => {
  const {
    control,
    watch,
    formState: { errors },
    setValue,
  } = useFormContext();
  const hookChoice = watch("hookChoice");
  const teeths = watch("teeths");
  const disabledTeeth = () => {
    const notDisabledTeeth = [...teeths.missingTeeth, ...teeths.toExtract];
    return getAllTeeths().filter(tooth => !notDisabledTeeth.includes(tooth));
  };
  useEffect(() => {
    if (
      !product?.removableItem?.length ||
      !product?.removableItem?.[0]?.partialDenture
    )
      return;

    setValue(
      "hookChoice",
      product.removableItem[0].partialDenture.hooksTeeth.length > 0
        ? HookPlacementBy.DOCTOR
        : HookPlacementBy.JASPER
    );
    setValue(
      "hooksPosition",
      product.removableItem[0].partialDenture.hooksTeeth
    );
    setValue(
      "hooksMaterial",
      product.removableItem[0].partialDenture.hooksMaterial?.id
    );

    const options = [];
    if (product.removableItem[0].partialDenture.metalMesh)
      options.push("METAL_MESH");
    if (product.removableItem[0].partialDenture.palatalMetalBand)
      options.push("PALATAL_METAL_BAND");
    if (product.removableItem[0].partialDenture.lingualMetalBand)
      options.push("LINGUAL_METAL_BAND");
    setValue("options", options);
  }, [product]);
  return (
    <Box pt="lg">
      <Title
        title="Détails concernant les crochets"
        size={3}
        mb="md"
      />
      <Controller
        control={control}
        name="hookChoice"
        rules={{ required: "Veuillez sélectionner une option" }}
        render={({ field }) => (
          <ButtonRadioSelect
            options={[
              {
                label: "Jasper choisit pour vous",
                value: HookPlacementBy.JASPER,
                description:
                  "Nous positionnons les crochets de la façon la plus esthétique possible",
              },
              {
                label: "Choix du praticien",
                value: HookPlacementBy.DOCTOR,
                description:
                  "Sélectionnez la ou les dents où positionner les crochets",
              },
            ]}
            setSelected={value => field.onChange(value as string)}
            selected={field.value}
            label="Postionnement"
          />
        )}
      />
      {errors.hookChoice && (
        <span style={{ color: "red", fontSize: "14px" }}>
          {errors.hookChoice.message as string}
        </span>
      )}
      {hookChoice === HookPlacementBy.DOCTOR && (
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            alignItems: "center",
          }}
        >
          <Controller
            control={control}
            name="hooksPosition"
            rules={{ required: "Veuillez sélectionner une position" }}
            render={({ field }) => (
              <DentalArchesSvg
                teeth={field.value ?? []}
                onClick={tooth => {
                  const teeth = field.value ?? [];
                  if (teeth.includes(tooth)) {
                    field.onChange(teeth.filter(t => t !== tooth));
                  } else {
                    field.onChange([...teeth, tooth]);
                  }
                }}
                newSelectionColor={STYLE.primary}
                notSelectedColor={"white"}
                disabledTeeth={disabledTeeth()}
              />
            )}
          />
          {errors.hooksPosition && (
            <span style={{ color: "red", fontSize: "14px" }}>
              {errors.hooksPosition.message as string}
            </span>
          )}
        </Box>
      )}
      <Controller
        control={control}
        rules={{ required: "Veuillez sélectionner un matériau" }}
        name="hooksMaterial"
        render={({ field }) => (
          <ButtonRadioSelect
            options={[
              { label: "Métal", value: ItemMaterials.METAL_NON_PRECIOUS },
              {
                label: "Valplast - option esthétique (crochet rose)",
                value: ItemMaterials.VALPLAST,
              },
              {
                label: "Acetal - option esthétique (crochet blanc)",
                value: ItemMaterials.ACETAL,
              },
            ]}
            setSelected={value => field.onChange(value)}
            selected={field.value}
            label="Matériau des crochets"
          />
        )}
      />
      {errors.hooksMaterial && (
        <span style={{ color: "red", fontSize: "14px" }}>
          {errors.hooksMaterial.message as string}
        </span>
      )}
      <Controller
        control={control}
        name="options"
        render={({ field }) => (
          <ButtonRadioSelect
            multiple
            options={[
              {
                label: "Grille de renfort métallique",
                value: "METAL_MESH",
              },
              {
                label: "Bandeau métallique lingual",
                value: "LINGUAL_METAL_BAND",
              },
              {
                label: "Bandeau métallique palatal",
                value: "PALATAL_METAL_BAND",
              },
            ]}
            setSelected={value => {
              const selected = field.value ?? [];
              if (selected.includes(value)) {
                field.onChange(selected.filter(o => o !== value));
              } else {
                field.onChange([...selected, value]);
              }
            }}
            selected={field.value ?? []}
            label="Options"
          />
        )}
      />
    </Box>
  );
};
